// noinspection DuplicatedCode
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return {};
  }
};

const getAuthHeaders = () => {
  const user = getUser();
  return {
    Authorization: "Bearer " + user.Token,
    Accept: "*/*",
  };
};

const handleResponse = (response, thunkAPI) => {
  if (response.status === 200) {
    return response.data;
  }
  return thunkAPI.rejectWithValue(response.data);
};

const handleError = (error, thunkAPI) => {
  return thunkAPI.rejectWithValue(
    error.response?.data || { message: error.message || "Network error" }
  );
};

export const getKioskDeviceList = createAsyncThunk(
  "kioskDevice/get",
  async ({ search = "", page = 1, page_size = 20 }, thunkAPI) => {
    const user = getUser();
    if (!user.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("page", page);
    params.append("page_size", page_size);

    const url = `${process.env.REACT_APP_API_URL}hrd/GetKioskDeviceList?${params.toString()}`;
    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return { ...response.data, currentPage: page, pageSize: page_size };
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const generateKioskDeviceCode = createAsyncThunk(
  "kioskDevice/generate",
  async ({ Nama, LokasiTugasID }, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const formData = new FormData();
    formData.append("Nama", Nama);
    if (LokasiTugasID) formData.append("LokasiTugasID", LokasiTugasID);

    const url = process.env.REACT_APP_API_URL + "hrd/GenerateKioskDeviceCode";
    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const revokeKioskDevice = createAsyncThunk(
  "kioskDevice/revoke",
  async (id, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const formData = new FormData();
    formData.append("id", id);
    const url = process.env.REACT_APP_API_URL + "hrd/RevokeKioskDevice";
    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const unpairKioskDevice = createAsyncThunk(
  "kioskDevice/unpair",
  async (id, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    const formData = new FormData();
    formData.append("id", id);
    const url = process.env.REACT_APP_API_URL + "hrd/UnpairKioskDevice";
    try {
      const response = await axios.post(url, formData, { headers: getAuthHeaders() });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

const initialState = {
  data: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  isGetFetching: false,
  isGetError: false,
  isGenerateFetching: false,
  isActionFetching: false,
  lastGeneratedCode: null,
  message: "",
  errorMessage: "",
};

export const kioskDeviceSlice = createSlice({
  name: "kioskDevice",
  initialState,
  reducers: {
    clearKioskDeviceMessage: (state) => {
      state.message = "";
      state.errorMessage = "";
    },
    clearLastGeneratedCode: (state) => {
      state.lastGeneratedCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKioskDeviceList.pending, (state) => {
        state.isGetFetching = true;
        state.isGetError = false;
      })
      .addCase(getKioskDeviceList.fulfilled, (state, { payload }) => {
        state.isGetFetching = false;
        const d = payload.data || {};
        state.data = d.data || [];
        state.pagination = {
          currentPage: payload.currentPage || d.page || 1,
          pageSize: payload.pageSize || d.page_size || 20,
          totalCount: d.total_count || 0,
          totalPages: d.total_pages || 0,
          hasNext: d.has_next || false,
          hasPrev: d.has_prev || false,
        };
      })
      .addCase(getKioskDeviceList.rejected, (state, { payload }) => {
        state.isGetFetching = false;
        state.isGetError = true;
        state.errorMessage = payload?.message || "Gagal mengambil data device";
        state.data = [];
      })
      .addCase(generateKioskDeviceCode.pending, (state) => {
        state.isGenerateFetching = true;
      })
      .addCase(generateKioskDeviceCode.fulfilled, (state, { payload }) => {
        state.isGenerateFetching = false;
        state.message = payload.message;
        state.lastGeneratedCode = payload.data;
      })
      .addCase(generateKioskDeviceCode.rejected, (state, { payload }) => {
        state.isGenerateFetching = false;
        state.errorMessage = payload?.message || "Gagal membuat kode perangkat";
      })
      .addCase(revokeKioskDevice.pending, (state) => {
        state.isActionFetching = true;
      })
      .addCase(revokeKioskDevice.fulfilled, (state, { payload }) => {
        state.isActionFetching = false;
        state.message = payload.message;
      })
      .addCase(revokeKioskDevice.rejected, (state, { payload }) => {
        state.isActionFetching = false;
        state.errorMessage = payload?.message || "Gagal menonaktifkan device";
      })
      .addCase(unpairKioskDevice.pending, (state) => {
        state.isActionFetching = true;
      })
      .addCase(unpairKioskDevice.fulfilled, (state, { payload }) => {
        state.isActionFetching = false;
        state.message = payload.message;
      })
      .addCase(unpairKioskDevice.rejected, (state, { payload }) => {
        state.isActionFetching = false;
        state.errorMessage = payload?.message || "Gagal melepas pairing";
      });
  },
});

export const { clearKioskDeviceMessage, clearLastGeneratedCode } = kioskDeviceSlice.actions;

export const kioskDeviceSelector = (state) => {
  const s = state.kioskDevice || {};
  return {
    data: s.data || [],
    pagination: s.pagination || initialState.pagination,
    isGetFetching: s.isGetFetching || false,
    isGenerateFetching: s.isGenerateFetching || false,
    isActionFetching: s.isActionFetching || false,
    lastGeneratedCode: s.lastGeneratedCode || null,
    message: s.message || "",
    errorMessage: s.errorMessage || "",
  };
};

export default kioskDeviceSlice.reducer;
